<template>
  <div v-if="loading">
    <div class="skeleton-loader-wrapper">
      <SkeletonLoader :loadingText="loadingText" />
    </div>
  </div>
  <div v-else class="account-view-container">
    <MainHeader :title="'Account'">
      <template #right>
        <div
          class="help-beacon"
          @click="openBeacon"
          title="Get Help"
          role="button"
          aria-label="Get Help"
        >
          <font-awesome-icon
            :icon="['fas', 'question']"
            class="help-beacon-icon"
          />
        </div>
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <!-- User greeting card -->
      <div class="user-hero">
        <div class="user-avatar">
          <font-awesome-icon :icon="['fas', 'user']" class="user-avatar-icon" />
        </div>
        <div class="user-info">
          <p class="user-name">
            {{
              store.user.preferredName || store.user.firstName || 'Hey there'
            }}
          </p>
          <p class="user-email">{{ store.user.email }}</p>
        </div>
      </div>

      <!-- Settings list -->
      <div class="settings-section">
        <span class="section-label">Account</span>

        <div class="settings-card">
          <div
            class="settings-row"
            @click="router.push({ name: 'UserProfile' })"
          >
            <div class="settings-icon-wrap purple">
              <font-awesome-icon
                :icon="['fas', 'user']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">User Profile</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
          <div
            class="settings-row"
            @click="router.push({ name: 'ConnectedAccounts' })"
          >
            <div class="settings-icon-wrap teal">
              <font-awesome-icon
                :icon="['fas', 'building-columns']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">Connected Accounts</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
        </div>
      </div>

      <div class="settings-section">
        <span class="section-label">Preferences</span>

        <div class="settings-card">
          <div class="settings-row" @click="router.push({ name: 'Settings' })">
            <div class="settings-icon-wrap grey">
              <font-awesome-icon
                :icon="['fas', 'gear']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">Settings</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
        </div>
      </div>

      <div class="settings-section">
        <span class="section-label">Tools</span>

        <div class="settings-card">
          <div
            class="settings-row"
            @click="router.push({ name: 'Calculators' })"
          >
            <div class="settings-icon-wrap coral">
              <font-awesome-icon
                :icon="['fas', 'calculator']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">Calculators</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
        </div>
      </div>

      <div class="settings-section">
        <span class="section-label">Help & Legal</span>

        <div class="settings-card">
          <div class="settings-row" @click="router.push({ name: 'Support' })">
            <div class="settings-icon-wrap blue">
              <font-awesome-icon
                :icon="['fas', 'circle-question']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">Support</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
          <div class="settings-row" @click="router.push({ name: 'Legal' })">
            <div class="settings-icon-wrap grey">
              <font-awesome-icon
                :icon="['fas', 'file-contract']"
                class="settings-icon"
              />
            </div>
            <span class="settings-label">Legal</span>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="settings-chevron"
            />
          </div>
        </div>
      </div>

      <button class="logout-btn w-100 mt-2" @click="handleLogout">
        Log Out
      </button>
    </div>

    <div class="app-version">v{{ appVersion }}</div>
  </div>
</template>

<script>
import { onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { useMyStore } from '../../store';
import MainHeader from '../../components/View-Header.vue';
import { Capacitor } from '@capacitor/core';
import SkeletonLoader from '../../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, SkeletonLoader },
  setup() {
    const router = useRouter();
    const { logout } = useAuth0();
    const appVersion = import.meta.env.VITE_APP_VERSION;
    const store = useMyStore();
    // const app = getCurrentInstance()?.appContext.app;
    // const pinia = app?._context.provides.pinia;
    // const isNative = (Capacitor.getPlatform?.() ?? 'web') !== 'web';
    // const bundleId = import.meta.env.VITE_BUNDLE_ID;
    // const domain = import.meta.env.VITE_AUTH0_DOMAIN;
    // const logoutReturnTo = `${bundleId}://${domain}/ios/${bundleId}/callback`;
    const loading = ref(false);
    const loadingText = ref('Logging Out');

    // const resetAllPiniaStores = () => {
    //   if (!pinia) return;
    //   Object.values(pinia.state.value).forEach((_, storeId) => {
    //     const store = pinia._s.get(storeId);
    //     if (store && typeof store.$reset === 'function') store.$reset();
    //   });
    // };

    // const clearIndexedDb = async () => {
    //   if (!('indexedDB' in window)) return;
    //   if (indexedDB.databases) {
    //     const dbs = await indexedDB.databases();
    //     await Promise.all(
    //       dbs.map((db) => {
    //         if (db.name) {
    //           return new Promise((res) => {
    //             const req = indexedDB.deleteDatabase(db.name);
    //             req.onsuccess = req.onerror = req.onblocked = () => res();
    //           });
    //         }
    //       })
    //     );
    //   }
    // };

    const handleLogout = async () => {
      loading.value = true;
      loadingText.value = 'Logging out';
      await store.fullLogout(
        logout,
        router,
        Capacitor.isNativePlatform(),
        import.meta.env.VITE_BUNDLE_ID,
        import.meta.env.VITE_AUTH0_DOMAIN
      );
    };

    // const handleLogout = async () => {
    //   loading.value = true;
    //   loadingText.value = 'Logging out';
    //   console.log('user account logout start');
    //   console.log('1');
    //   try {
    //     try {
    //       await store.captureNetWorth();
    //       console.log('2');
    //     } catch (e) {
    //       console.warn('captureNetWorth failed (continuing):', e);
    //     }

    //     console.log('Calling Auth0 logout..');
    //     console.log(
    //       'Capacitor.isNativePlatform() ' + Capacitor.isNativePlatform()
    //     );

    //     store.updateUserLoggedIn(false);

    //     if (Capacitor.isNativePlatform()) {
    //       console.log('3');
    //       try {
    //         await Purchases.logOut();
    //         console.log('RevenueCat logout successful');
    //       } catch (e) {
    //         console.warn('RevenueCat logout failed (continuing):', e);
    //       }
    //       console.log('3.5');
    //       await logout({
    //         logoutParams: { returnTo: logoutReturnTo },
    //         openUrl: async (url) => {
    //           console.log('[Auth] Opening logout URL:', url);
    //           await Browser.open({ url });
    //         },
    //       });
    //       console.log('after logout close browser');
    //       try {
    //         await Browser.close();
    //       } catch {
    //         console.log('error closing browser');
    //       }
    //       console.log('4');
    //     } else {
    //       await logout({ logoutParams: { returnTo: window.location.origin } });
    //       console.log('5');
    //     }

    //     console.log('Auth0 logout completed');
    //     console.log('6');

    //     try {
    //       console.log('7');
    //       localStorage.clear();
    //       console.log('8');
    //       sessionStorage.clear();
    //       console.log('9');
    //     } catch (e) {
    //       console.log('10');
    //       console.warn('Storage clear failed:', e);
    //     }

    //     console.log('11');
    //     await clearIndexedDb();
    //     console.log('12');

    //     if (isNative) {
    //       console.log('13');
    //       try {
    //         console.log('14');
    //         await Preferences.clear();
    //         console.log('15');
    //       } catch (e) {
    //         console.log('16');
    //         console.warn('Preferences clear failed:', e);
    //       }
    //       console.log('17');
    //     }

    //     console.log('18');
    //     resetAllPiniaStores();
    //     console.log('19');
    //     console.log('after clearing stuff out');

    //     router.replace({ name: 'SignIn' });
    //   } catch (e) {
    //     console.log('22');
    //     console.error('Logout error:', e);
    //     localStorage.clear();
    //     console.log('23');
    //     sessionStorage.clear();
    //     console.log('24');
    //     router.replace({ name: 'SignIn' });
    //     loading.value = false;
    //   }
    // };

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=account');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=account');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      router,
      store,
      handleLogout,
      appVersion,
      openBeacon,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.account-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content {
  flex-grow: 1;
}

.help-beacon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-purple);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.help-beacon:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-purple-lg);
}
.help-beacon-icon {
  color: white;
  font-size: 13px;
}

.user-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  border-radius: var(--radius-lg);
  padding: 1.1rem 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .user-hero {
  background: #212e3e;
  border-color: #2a3a4e;
}

.user-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: var(--shadow-purple);
}
.user-avatar-icon {
  color: white;
  font-size: 20px;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.user-name {
  font-size: 1rem;
  font-weight: 700;
  color: #004751;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .user-name {
  color: white;
}
.user-email {
  font-size: 0.75rem;
  color: #aaa;
  margin: 0;
}

.settings-section {
  margin-bottom: 1.25rem;
}

.settings-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .settings-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.settings-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.9rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f8f8;
  transition: background var(--transition-fast);
}
.dark-theme .settings-row {
  border-bottom-color: #2a3a4e;
}
.settings-row:last-child {
  border-bottom: none;
}
.settings-row:hover {
  background: #fafafa;
}
.dark-theme .settings-row:hover {
  background: #1a2535;
}

.settings-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.settings-icon-wrap.purple {
  background: var(--purple-50);
}
.settings-icon-wrap.teal {
  background: var(--teal-50);
}
.settings-icon-wrap.blue {
  background: #eef4ff;
}
.settings-icon-wrap.grey {
  background: #f4f4f4;
}
.settings-icon-wrap.coral {
  background: #fdeee7;
}
.dark-theme .settings-icon-wrap.purple {
  background: #1e1a40;
}
.dark-theme .settings-icon-wrap.teal {
  background: #0d2420;
}
.dark-theme .settings-icon-wrap.blue {
  background: #0d1a30;
}
.dark-theme .settings-icon-wrap.grey {
  background: #1a2535;
}
.dark-theme .settings-icon-wrap.coral {
  background: #2e1d18;
}

.settings-icon {
  font-size: 14px;
  color: var(--color-accent-purple);
}
.settings-icon-wrap.teal .settings-icon {
  color: var(--color-accent-teal);
}
.settings-icon-wrap.blue .settings-icon {
  color: #3b82f6;
}
.settings-icon-wrap.grey .settings-icon {
  color: #888;
}
.settings-icon-wrap.coral .settings-icon {
  color: #d85a30;
}

.settings-label {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 600;
  color: #213547;
}
.dark-theme .settings-label {
  color: white;
}

.settings-chevron {
  font-size: 0.72rem;
  color: #ccc;
}
.dark-theme .settings-chevron {
  color: #4a5a6e;
}

.logout-btn {
  border: 2px solid #d85a30;
  background: white;
  color: #d85a30;
  border-radius: var(--radius-pill);
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    transform var(--transition-fast);
}
.logout-btn:hover {
  background: #faece7;
  transform: translateY(-1px);
}
.logout-btn:active {
  transform: scale(0.97);
}
.dark-theme .logout-btn {
  background: transparent;
  color: #f09595;
  border-color: #f09595;
}

.app-version {
  text-align: center;
  font-size: 0.72rem;
  color: #bbb;
  padding: 0.75rem 0 1rem;
}
</style>

<!-- <template>
  <div v-if="loading">
    <div class="skeleton-loader-wrapper">
      <SkeletonLoader :loadingText="loadingText" />
    </div>
  </div>  
  <div v-else class="account-view-container">
    <MainHeader :title="'User Account'">
      <template #right>
        <div class="beacon-launcher" @click="openBeacon" title="Get Help">
          <font-awesome-icon :icon="['fas', 'question']" class="beacon-icon" />
        </div>
      </template>
    </MainHeader>
    <div class="view content">
      <div class="content-padding">
        <div
          class="settings-list mb-2"
          @click="router.push({ name: 'UserProfile' })"
        >
          <div><font-awesome-icon :icon="['fas', 'user']" /></div>
          <div>User Profile</div>
        </div>
        <div
          class="settings-list mb-2"
          @click="router.push({ name: 'ConnectedAccounts' })"
        >
          <div><font-awesome-icon :icon="['fas', 'building-columns']" /></div>
          <div>Accounts</div>
        </div>
        <div
          class="settings-list mb-2"
          @click="router.push({ name: 'Settings' })"
        >
          <div><font-awesome-icon :icon="['fas', 'gear']" /></div>
          <div>Settings</div>
        </div>
        <div
          class="settings-list mb-2"
          @click="router.push({ name: 'Support' })"
        >
          <div><font-awesome-icon :icon="['fas', 'circle-question']" /></div>
          <div>Support</div>
        </div>
        <div class="settings-list mb-2" @click="router.push({ name: 'Legal' })">
          <div><font-awesome-icon :icon="['fas', 'file-contract']" /></div>
          <div>Legal</div>
        </div>
        <div class="mt-1">
          <button class="red-button mt-4 w-100" @click="handleLogout">
            Log Out
          </button>
        </div>
      </div>
    </div>
    <div class="app-version">App version: {{ appVersion }}</div>
  </div>
</template>

<script>
import { onBeforeUnmount, getCurrentInstance, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { useMyStore } from '../../store';

import MainHeader from '../../components/View-Header.vue';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import { fullLogout } from '../../utils/logout';

export default {
  components: {
    MainHeader,
    SkeletonLoader
  },
  setup() {
    const router = useRouter();
    const { logout } = useAuth0();
    const appVersion = import.meta.env.VITE_APP_VERSION;
    const store = useMyStore();
    const API_BASE = import.meta.env.VITE_BASE_URL;
    const app = getCurrentInstance()?.appContext.app;
    const pinia = app?._context.provides.pinia;
    const isNative = (Capacitor.getPlatform?.() ?? 'web') !== 'web';
    const bundleId = import.meta.env.VITE_BUNDLE_ID;
    const domain = import.meta.env.VITE_AUTH0_DOMAIN;
    const logoutReturnTo = `${bundleId}://${domain}/ios/${bundleId}/callback`;
    const loading = ref(false);
    const loadingText = ref('Logging Out');

    const resetAllPiniaStores = () => {
      if (!pinia) return;
      Object.values(pinia.state.value).forEach((_, storeId) => {
        const store = pinia._s.get(storeId);
        if (store && typeof store.$reset === 'function') {
          store.$reset();
        }
      });
    };

    const clearIndexedDb = async () => {
      if (!('indexedDB' in window)) return;
      if (indexedDB.databases) {
        const dbs = await indexedDB.databases();
        await Promise.all(
          dbs.map((db) => {
            if (db.name) {
              return new Promise((res) => {
                const req = indexedDB.deleteDatabase(db.name);
                req.onsuccess = req.onerror = req.onblocked = () => res();
              });
            }
          })
        );
      }
    };

    const handleLogout = async () => {
      loading.value = true;
      try {
        await fullLogout({ auth0Logout: logout, store, router, pinia });
      } finally {
        loading.value = false;
      }
    };

    // const handleLogout = async () => {
    //   loading.value = true;
    //   loadingText.value = 'Logging out';
    //   console.log('user account logout start');
    //   console.log('1');
    //   try {
    //     try {
    //       await store.captureNetWorth();
    //       console.log('2');
    //     } catch (e) {
    //       console.warn('captureNetWorth failed (continuing):', e);
    //     }

    //     console.log('Calling Auth0 logout...');

    //     console.log(
    //       'Capacitor.isNativePlatform() ' + Capacitor.isNativePlatform()
    //     );

    //     // await logout({
    //     //   logoutParams: {
    //     //     federated: Capacitor.isNativePlatform() ? true : false,
    //     //     returnTo: Capacitor.isNativePlatform() ? `${import.meta.env.VITE_BUNDLE_ID}://callback` : window.location.origin
    //     //   }
    //     // });

    //     store.updateUserLoggedIn(false);

    //     if (Capacitor.isNativePlatform()) {
    //       console.log('3');
    //       await logout({
    //         logoutParams: {
    //           // federated: true,
    //           // returnTo: `${import.meta.env.VITE_BUNDLE_ID}://callback`
    //           returnTo: logoutReturnTo
    //         },
    //         openUrl: async (url) => {
    //           console.log('[Auth] Opening logout URL:', url);
    //           await Browser.open({ url });
    //         }
    //       });
    //       console.log('after logout close browser');
    //       try { await Browser.close(); } catch {}
    //       console.log('4');
    //     } else {
    //       await logout({
    //         logoutParams: {
    //           returnTo: window.location.origin,
    //         },
    //       });
    //       console.log('5');
    //     }

    //     console.log('Auth0 logout completed');

    //     // await new Promise((resolve) => setTimeout(resolve, 500));

    //     console.log('6');

    //     try {
    //       console.log('7');
    //       localStorage.clear();
    //       console.log('8');
    //       sessionStorage.clear();
    //       console.log('9');
    //     } catch (e) {
    //       console.log('10');
    //       console.warn('Storage clear failed:', e);
    //     }

    //     console.log('11');
    //     await clearIndexedDb();
    //     console.log('12');

    //     if (isNative) {
    //       console.log('13');
    //       try {
    //         console.log('14')
    //         await Preferences.clear();
    //         console.log('15');
    //       } catch (e) {
    //         console.log('16');
    //         console.warn('Preferences clear failed:', e);
    //       }
    //       console.log('17');
    //     }

    //     console.log('18');
    //     resetAllPiniaStores();
    //     console.log('19');

    //     console.log('after clearing stuff out');

    //     if (Capacitor.isNativePlatform()) {
    //       console.log('20');
    //       router.replace({ name: 'SignIn' });
    //     } else {
    //       console.log('21');
    //       router.replace({ name: 'SignIn' });
    //     }
    //   } catch (e) {
    //     console.log('22');
    //     console.error('Logout error:', e);
    //     localStorage.clear();

    //     console.log('23');
    //     sessionStorage.clear();
    //     console.log('24');
    //     router.replace({ name: 'SignIn' });
    //     loading.value = false;
    //   }
    // };


    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=account');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=account');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      router,
      handleLogout,
      appVersion,
      openBeacon,
      loading,
      loadingText
    };
  },
};
</script>
<style scoped>
.settings-list {
  display: grid;
  grid-template-columns: 10% 90%;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
  padding: 1em;
  border-radius: 5px;
  cursor: pointer;
}

.light-theme .settings-list {
  background: white;
}

.dark-theme .settings-list {
  background: #212e3e;
  color: white;
}

.account-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex-grow: 1;
}

.app-version {
  text-align: center;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 1rem;
  justify-content: center;
}
</style> -->
